import { FC, useCallback, useState } from "react";
import * as React from "react";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { FormikProvider, useFormik, FormikConfig } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { SubmittedAddressOutput } from "apiIntegrations/campaigns/SubmittedAddress.interface";
import { claim } from "validators/client/collectAddresses";

import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import CustomOutlinedInput from "components/CustomOutlinedInput";
import CustomButton from "components/CustomButton";
import Recaptcha from "components/Recaptcha";

import { STORAGE } from "enums/storage";

import { API_ROUTES } from "config/routes";

import { getObjectItem, setObjectItem } from "utils/storage";

import fetcher from "lib/fetchJson";

interface AddressSubmission {
  ethAddress: string;
}

const initialState: AddressSubmission = {
  ethAddress: "",
};

interface SectionCollectAddressesProps {
  canMint: boolean;
  isMinted: boolean;
  boardId: number;
  html: string;
}

const SectionCollectAddresses: FC<SectionCollectAddressesProps> = ({
  canMint,
  isMinted,
  boardId,
  html,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const storageData = getObjectItem<AddressSubmission>(
    STORAGE.COLLECT_ADDRESSES,
  );
  const onSubmit: FormikConfig<AddressSubmission>["onSubmit"] = useCallback(
    async (values) => {
      try {
        if (!executeRecaptcha) {
          return Promise.reject({
            message: "Execute recaptcha not yet available",
          });
        }
        const captchaToken = await executeRecaptcha("COLLECT_ADDRESSES");
        setObjectItem(STORAGE.COLLECT_ADDRESSES, values);
        const { data } = await fetcher<{ data: SubmittedAddressOutput }>(
          API_ROUTES.COLLECT_ADDRESSES,
          {
            ...values,
            boardId,
            captchaToken,
          },
          "POST",
        );
        setIsSubmitted(!!data.success);
      } catch (e) {
        toast.error(e.data.message ?? "Something went wrong.");
      }
    },
    [executeRecaptcha, boardId],
  );

  const formik = useFormik({
    initialValues: storageData ?? initialState,
    validationSchema: toFormikValidationSchema(claim),
    validateOnChange: false,
    onSubmit: onSubmit,
  });

  if (isMinted) {
    if (isSubmitted) {
      return (
        <Box>
          <InfoBox type={INFO_BOX_TYPE.SUCCESS}>
            Congratulations! You've successfully submitted your ETH address!
          </InfoBox>
        </Box>
      );
    }
    return (
      <FormikProvider value={formik}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Typography dangerouslySetInnerHTML={{ __html: html }} mb={2} />
          <CustomOutlinedInput
            label="ETH Address"
            inputProps={{
              id: "ethAddress",
              placeholder: "ETH Address",
              value: formik.values.ethAddress,
              onChange: formik.handleChange,
            }}
            formControlProps={{
              fullWidth: true,
              margin: "normal",
            }}
            error={formik.errors.ethAddress}
          />
          <Recaptcha boxProps={{ mb: 2 }} />

          <CustomButton
            sx={{
              textTransform: "uppercase",
              ml: "auto",
              display: "block",
            }}
            type="submit"
            isLoading={formik.isSubmitting}
            disabled={formik.isSubmitting || !canMint}
            color="warning"
          >
            Submit
          </CustomButton>
        </Box>
      </FormikProvider>
    );
  }

  return (
    <Box width="100%">
      <Typography paragraph>
        Upon the mint, you will be able to submit your ETH address to claim your
        extras!
      </Typography>
    </Box>
  );
};

export default SectionCollectAddresses;
